import { FormEvent, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { login } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { APIClientError } from '@/api/client'

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const setSession = useAuthStore((s) => s.setSession)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function validate(): string | null {
    if (!email.trim()) return 'Email address is required.'
    if (!/^\S+@\S+\.\S+$/.test(email)) return 'Please enter a valid email address.'
    if (!password) return 'Password is required.'
    return null
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const result = await login(email.trim(), password)
      setSession(result.accessToken, result.admin)
      const redirectTo = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard'
      navigate(redirectTo, { replace: true })
    } catch (err) {
      if (err instanceof APIClientError) {
        if (err.code === 'ACCOUNT_LOCKED') {
          setError('This account has been locked due to multiple failed attempts. Please contact support.')
        } else if (err.code === 'RATE_LIMITED') {
          setError('Too many login attempts. Please wait a moment and try again.')
        } else {
          setError('Invalid email or password.')
        }
      } else {
        setError('Unable to connect to server. Please check your network and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
      {/* Background Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-blue-200/25 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        {/* Brand Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3.5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-600 to-blue-700 shadow-xl text-white font-display text-2xl font-extrabold ring-4 ring-sky-100">
            V
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            VSign <span className="text-sky-600">Root Admin</span>
          </h1>
          <p className="mt-1 text-xs font-semibold text-slate-500">
            Central Management & Governance Portal
          </p>

          <div className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-bold text-sky-800 shadow-2xs">
            <span className="h-2 w-2 animate-pulse rounded-full bg-sky-600" />
            Root Administrator Access
          </div>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md p-8 shadow-xl"
          noValidate
        >
          <div className="mb-6">
            <h2 className="font-display text-lg font-bold text-slate-900">Sign In</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Enter your administrator credentials to access the portal.
            </p>
          </div>

          <div className="mb-5">
            <label htmlFor="email" className="mb-1.5 block text-xs font-bold text-slate-700">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-xs text-slate-900 font-medium placeholder-slate-400 transition-all focus:border-sky-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
              placeholder="admin@veloscalar.com"
            />
          </div>

          <div className="mb-6">
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="password" className="block text-xs font-bold text-slate-700">
                Password
              </label>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 pr-10 text-xs text-slate-900 font-medium placeholder-slate-400 transition-all focus:border-sky-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-semibold focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/90 p-3 text-xs text-rose-800 font-semibold shadow-2xs"
            >
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-sky-600 to-blue-700 py-3 text-xs font-bold text-white shadow-md transition-all duration-200 hover:shadow-lg hover:from-sky-500 hover:to-blue-600 disabled:opacity-50"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {submitting ? 'Signing In…' : 'Sign In'}
              {!submitting && <span className="transition-transform group-hover:translate-x-1">→</span>}
            </span>
          </button>

          <div className="mt-6 text-center text-[11px] font-medium text-slate-400">
            Protected by Enterprise Security & Access Controls
          </div>
        </form>
      </div>
    </div>
  )
}
